import { Card, Grid, SegmentedControl, Text, TextInput } from '@mantine/core';
import DatePickerControl from '../DatePicker';
import SegmentSelector from '../Segments/SegmentSelector';

const AudienceBannerForm = ({ banner, handleStateChange, bannerTypeOptions }) => {
  let reentryConfig = '';
  if (banner.type === 'showUntilClick') {
    reentryConfig = (
      <>
        <Grid mb="md" data-tour="bannerAfterClickSelect">
          <Grid.Col span={{ base: 12, sm: 4 }}>
            <Text size="sm" fw={500}>
              After click
            </Text>
          </Grid.Col>
          <Grid.Col span={{ base: 12, sm: 8 }}>
            <SegmentedControl
              value={String(banner.reentrable)}
              onChange={(value) => handleStateChange({ target: { name: 'reentrable', value: value === 'true' } })}
              data={[
                { label: 'Never show again to this user', value: 'false' },
                { label: 'Show again after cooldown', value: 'true' },
              ]}
            />
            {banner.reentrable && (
              <Grid>
                <Grid.Col span={{ base: 12 }}>
                  <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                    <TextInput
                      size="sm"
                      name="reentryCooldownInDays"
                      type="number"
                      min="0"
                      step="1"
                      value={banner.reentryCooldownInDays || ''}
                      onChange={(e) =>
                        handleStateChange({
                          target: { name: e.target.name, value: e.target.value === '' ? null : e.target.value },
                        })
                      }
                      rightSection={<Text size="xs">day(s)</Text>}
                      rightSectionWidth={50}
                    />
                    <TextInput
                      size="sm"
                      name="reentryCooldownInHours"
                      type="number"
                      min="0"
                      step="1"
                      value={banner.reentryCooldownInHours || ''}
                      onChange={(e) =>
                        handleStateChange({
                          target: { name: e.target.name, value: e.target.value === '' ? null : e.target.value },
                        })
                      }
                      rightSection={<Text size="xs">hour(s) after last entry</Text>}
                      rightSectionWidth={130}
                    />
                  </div>
                </Grid.Col>
              </Grid>
            )}
          </Grid.Col>
        </Grid>
      </>
    );
  }

  return (
    <Card data-tour="bannerSegmentContainer" withBorder>
      <Card.Section inheritPadding py="sm">
        <SegmentSelector
          title="Behavioral segment"
          description="This determines who will see your banner based on aggregated behavior. Behavioral segments are evaluated continuously and are not real-time ."
          segmentId={banner.segmentId}
          onSelect={(segment) => handleStateChange({ target: { name: 'segmentId', value: segment?.id } })}
          dataTour="bannerSegmentSelect"
          campaignScope="marketing"
          scope="behavior"
          forType="banner"
          sizeTitle={4}
          sizeBody={8}
        />
        <Grid mb="md" data-tour="bannerSegmentRelationship">
          <Grid.Col span={{ base: 12, sm: 4 }}>
            <Text size="sm" fw={500}>
              Action
            </Text>
          </Grid.Col>
          <Grid.Col span={{ base: 12, sm: 8 }}>
            <SegmentedControl
              value={String(banner.inverseSegmentSelection)}
              onChange={(value) =>
                handleStateChange({ target: { name: 'inverseSegmentSelection', value: value === 'true' } })
              }
              data={[
                { label: 'Show', value: 'false' },
                { label: 'Hide', value: 'true' },
              ]}
            />
            <Text size="sm" c="dimmed">
              This controls whether the banner will be shown or hidden for members of the chosen segment. For example if
              you select Hide this means that all members of this segment won't see the banner and all other members
              will see it and vice versa.
            </Text>
          </Grid.Col>
        </Grid>
        <hr />
        <Grid mb="md" data-tour="bannerSegmentRelationship">
          <Grid.Col span={{ base: 12, sm: 4 }}></Grid.Col>
          <Grid.Col span={{ base: 12, sm: 8 }}>
            <SegmentedControl
              value={banner.segmentRelationship ?? ''}
              onChange={(value) => handleStateChange({ target: { name: 'segmentRelationship', value } })}
              data={[
                { label: 'And', value: 'and' },
                { label: 'Or', value: 'or' },
              ]}
            />
          </Grid.Col>
        </Grid>
        <hr />
        <SegmentSelector
          title="Profile filter"
          dataTour="bannerProfileFilterSelect"
          description="This determines who will see your banner based on their current attributes. This filter is applied in real-time."
          segmentId={banner.realtimeSegmentId}
          onSelect={(segment) =>
            handleStateChange({ target: { name: 'realtimeSegmentId', value: segment?.id ?? null } })
          }
          campaignScope="marketing"
          scope="profileFilter"
          forType="banner"
          sizeTitle={4}
          sizeBody={8}
          optional={true}
        />
        <hr />
        <Grid mb="md" data-tour="bannerTypeSelect">
          <Grid.Col span={{ base: 12, sm: 4 }} />
          <Grid.Col span={{ base: 12, sm: 8 }}>
            <SegmentedControl
              value={banner.type ?? ''}
              onChange={(value) => handleStateChange({ target: { name: 'type', value } })}
              data={bannerTypeOptions}
            />
          </Grid.Col>
        </Grid>
        {reentryConfig}
        <Grid mb="md" data-tour="bannerShowWhenSelect">
          <Grid.Col span={{ base: 12, sm: 4 }} mb="md">
            <Text size="sm" fw={500}>
              Show when
            </Text>
          </Grid.Col>
          <Grid.Col span={{ base: 12, sm: 8 }}>
            <Grid>
              <Grid.Col span={{ base: 12, sm: 6 }}>
                <DatePickerControl
                  prepend="from"
                  size="sm"
                  showTimeSelect
                  placeholder="Any date"
                  value={banner.startTime}
                  onChange={(value) => handleStateChange({ target: { name: 'startTime', value } })}
                />
              </Grid.Col>
              <Grid.Col span={{ base: 12, sm: 6 }}>
                <DatePickerControl
                  prepend="to"
                  append="local time"
                  size="sm"
                  showTimeSelect
                  placeholder="Any date"
                  value={banner.endTime}
                  onChange={(value) => handleStateChange({ target: { name: 'endTime', value } })}
                />
              </Grid.Col>
            </Grid>
          </Grid.Col>
        </Grid>
      </Card.Section>
    </Card>
  );
};

export default AudienceBannerForm;
